const isEmptyHtml = (html) => {
  if (!html || typeof html !== 'string') return true;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text === '';
};

const stripHtmlToText = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const createNoteId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `note-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const nowIso = () => new Date().toISOString();

/**
 * @param {string} [title]
 * @param {{ isIndividual?: boolean }} [options]
 */
const createEmptyNote = (title = '', options = {}) => ({
  id: createNoteId(),
  title,
  body: '',
  isIndividual: Boolean(options.isIndividual),
  createdAt: nowIso(),
  updatedAt: nowIso(),
});

const normalizeNote = (item, index = 0) => {
  if (typeof item === 'string') {
    return {
      id: createNoteId(),
      title: '',
      body: item,
      isIndividual: false,
      createdAt: null,
      updatedAt: null,
    };
  }
  if (!item || typeof item !== 'object') {
    return createEmptyNote();
  }
  return {
    id: typeof item.id === 'string' && item.id ? item.id : `note-${index}-${createNoteId()}`,
    title: typeof item.title === 'string' ? item.title : '',
    body: typeof item.body === 'string' ? item.body : '',
    // Legacy notes without the flag behave as normal (public) notes.
    isIndividual: Boolean(item.isIndividual),
    createdAt: typeof item.createdAt === 'string' ? item.createdAt : null,
    updatedAt: typeof item.updatedAt === 'string' ? item.updatedAt : null,
  };
};

/**
 * Parse stored client notes into [{ id, title, body, isIndividual, createdAt, updatedAt }].
 * Supports new JSON array format and legacy plain text / HTML.
 */
const parseClientNotes = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item, index) => normalizeNote(item, index));
      }
    } catch {
      // fall through to legacy
    }
  }

  return [{
    id: 'legacy',
    title: '',
    body: raw,
    isIndividual: false,
    createdAt: null,
    updatedAt: null,
  }];
};

const noteHasContent = (note) =>
  Boolean(note?.title?.trim()) || !isEmptyHtml(note?.body);

const isIndividualNote = (note) => Boolean(note?.isIndividual);

const filterPublicNotes = (notes) =>
  (Array.isArray(notes) ? notes : []).filter((note) => !isIndividualNote(note));

const getNotePrimaryText = (note) => {
  const title = (note?.title || '').trim();
  if (title) return title;
  return stripHtmlToText(note?.body || '');
};

const serializeClientNotes = (notes) => {
  if (!Array.isArray(notes)) return '';
  const cleaned = notes
    .filter(noteHasContent)
    .map((note) => {
      const row = {
        id: note.id || createNoteId(),
        title: (note.title || '').trim(),
        body: isEmptyHtml(note.body) ? '' : note.body,
        isIndividual: Boolean(note.isIndividual),
      };
      if (note.createdAt) row.createdAt = note.createdAt;
      if (note.updatedAt) row.updatedAt = note.updatedAt;
      return row;
    });
  if (cleaned.length === 0) return '';
  return JSON.stringify(cleaned);
};

const hasClientNotesContent = (raw) => parseClientNotes(raw).some(noteHasContent);

/** True when there is at least one non-individual note with content. */
const hasPublicClientNotesContent = (raw) =>
  filterPublicNotes(parseClientNotes(raw)).some(noteHasContent);

const touchNote = (note, patch = {}) => ({
  ...note,
  ...patch,
  updatedAt: nowIso(),
  createdAt: note.createdAt || nowIso(),
});

export {
  isEmptyHtml,
  stripHtmlToText,
  createEmptyNote,
  createNoteId,
  parseClientNotes,
  serializeClientNotes,
  hasClientNotesContent,
  hasPublicClientNotesContent,
  noteHasContent,
  isIndividualNote,
  filterPublicNotes,
  getNotePrimaryText,
  touchNote,
};
